/**
 * XRP Wallet Connector using Xumm
 * Supports ALL XRPL wallets (Xumm, Gem Wallet, Crossmark, etc.)
 */

import { Xumm } from 'xumm';
import { toast } from 'sonner';

// Xumm client singleton
let xummClient: Xumm | null = null;
let connectedAddress: string | null = null;

/**
 * Initialize Xumm client
 * Requires XUMM API key from https://apps.xumm.dev/
 */
function getXummClient(): Xumm {
  if (!xummClient) {
    const apiKey = import.meta.env.VITE_XUMM_API_KEY;
    const apiSecret = import.meta.env.VITE_XUMM_API_SECRET;
    
    if (!apiKey || apiKey === 'YOUR_XUMM_API_KEY_HERE') {
      throw new Error('Xumm API key not configured. Get one from https://apps.xumm.dev/');
    }
    
    if (!apiSecret || apiSecret === 'YOUR_XUMM_API_SECRET_HERE') {
      throw new Error('Xumm API secret not configured. Get one from https://apps.xumm.dev/');
    }
    
    xummClient = new Xumm(apiKey, apiSecret);
  }
  return xummClient;
}

/**
 * Connect XRP wallet via Xumm
 * Works with mobile and browser
 */
export async function connectXRPWallet(): Promise<{ address: string }> {
  try {
    const xumm = getXummClient();
    
    // Create sign-in request
    const request = {
      txjson: {
        TransactionType: 'SignIn',
      },
    };
    
    // Create payload
    const payload = await xumm.payload.create(request);
    
    if (!payload || !payload.next || !payload.refs) {
      throw new Error('Failed to create Xumm sign-in request');
    }
    
    // Show QR code or deep link to user
    toast.info('XRP Wallet Connection', {
      description: 'Scan QR code with Xumm app or click the link to connect',
      duration: 10000,
    });
    
    // Open Xumm app (deep link for mobile, web for desktop)
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      window.location.href = payload.next.always; // Deep link
    } else {
      window.open(payload.next.always, '_blank'); // New tab
    }
    
    // Wait for user to sign in
    const result = await xumm.payload.subscribe(payload.uuid);
    
    if (!result || !result.signed || !result.user_token) {
      throw new Error('User rejected connection or sign-in failed');
    }
    
    // Get user's XRP address
    const userInfo = await xumm.user.account;
    
    if (!userInfo || !userInfo.account) {
      throw new Error('Failed to get XRP address from Xumm');
    }
    
    const address = userInfo.account;
    connectedAddress = address;
    
    toast.success('XRP Wallet Connected', {
      description: `Address: ${address.substring(0, 20)}...`,
      duration: 5000,
    });
    
    return { address };
    
  } catch (error: any) {
    console.error('XRP wallet connection error:', error);
    toast.error('XRP Connection Failed', {
      description: error.message || 'Failed to connect XRP wallet',
      duration: 5000,
    });
    throw error;
  }
}

/**
 * Disconnect XRP wallet
 */
export async function disconnectXRPWallet(): Promise<void> {
  try {
    connectedAddress = null;
    
    toast.info('XRP Wallet Disconnected', {
      description: 'Successfully disconnected from XRP wallet',
    });
  } catch (error) {
    console.error('XRP disconnect error:', error);
  }
}

/**
 * Get XRP balance
 * Requires XRP RPC endpoint
 */
export async function fetchXRPBalance(address: string): Promise<number> {
  try {
    const rpcUrl = 'https://xrplcluster.com/';
    
    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        method: 'account_info',
        params: [{
          account: address,
          ledger_index: 'current',
        }]
      }),
    });
    
    const data = await response.json();
    
    if (data.result && data.result.account_data && data.result.account_data.Balance) {
      // Convert drops to XRP (1 XRP = 1,000,000 drops)
      return parseInt(data.result.account_data.Balance) / 1e6;
    }
    
    return 0;
  } catch (error) {
    console.error('Failed to fetch XRP balance:', error);
    return 0;
  }
}

/**
 * Check if XRP wallet is connected
 */
export function isXRPWalletConnected(): boolean {
  return connectedAddress !== null;
}

